import { Component } from '@theme/component';

export const FAVORITES_KEY = 'vachtgenoot:favorites:v1';
export const FAVORITES_LIMIT = 40;
const CHANGE_EVENT = 'vg:favorites-change';
const EXCLUDED_HANDLE = 'cadeauverpakking-met-persoonlijk-kaartje';
const EXCLUDED_PRODUCT_ID = '12390785614088';
const EXCLUDED_VARIANT_ID = '59254702047496';

export function validHandle(value) {
  return typeof value === 'string' && value !== EXCLUDED_HANDLE && value.length <= 255 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

export function normalizeHandles(values) {
  if (!Array.isArray(values)) throw new TypeError('Invalid favorites');
  return [...new Set(values.filter(validHandle))].slice(0, FAVORITES_LIMIT);
}

export function readFavorites(storage) {
  try {
    const raw = storage().getItem(FAVORITES_KEY);
    if (raw === null) return { ok: true, handles: [] };
    if (typeof raw !== 'string' || raw.length > 32768) return { ok: false, reason: 'storage_error', handles: [] };
    const handles = normalizeHandles(JSON.parse(raw));
    if (JSON.stringify(handles) !== raw) return writeFavorites(storage, handles);
    return { ok: true, handles };
  } catch {
    return { ok: false, reason: 'storage_error', handles: [] };
  }
}

export function writeFavorites(storage, values) {
  try {
    const handles = normalizeHandles(values);
    const target = JSON.stringify(handles);
    storage().setItem(FAVORITES_KEY, target);
    if (storage().getItem(FAVORITES_KEY) !== target) return { ok: false, reason: 'storage_error' };
    return { ok: true, handles };
  } catch {
    return { ok: false, reason: 'storage_error' };
  }
}

export function toggleFavorite(storage, handle) {
  if (!validHandle(handle)) return { ok: false, reason: 'storage_error' };
  const current = readFavorites(storage);
  if (!current.ok) return current;
  const saved = current.handles.includes(handle);
  if (!saved && current.handles.length >= FAVORITES_LIMIT) return { ok: false, reason: 'limit' };
  const result = writeFavorites(storage, saved ? current.handles.filter((item) => item !== handle) : [handle, ...current.handles]);
  return result.ok ? { ...result, saved: !saved } : result;
}

export function removeFavorite(storage, handle) {
  const current = readFavorites(storage);
  if (!current.ok) return current;
  return writeFavorites(storage, current.handles.filter((item) => item !== handle));
}

export function clearFavorites(storage) {
  try {
    storage().removeItem(FAVORITES_KEY);
    if (storage().getItem(FAVORITES_KEY) !== null) return { ok: false, reason: 'storage_error' };
    return { ok: true, handles: [] };
  } catch {
    return { ok: false, reason: 'storage_error' };
  }
}

export function productURL(root, handle, origin, json = false) {
  if (!validHandle(handle)) throw new TypeError('Invalid handle');
  const base = new URL(root, origin);
  if (base.origin !== origin || base.search || base.hash || !base.pathname.endsWith('/')) throw new TypeError('Invalid route');
  return new URL(`products/${encodeURIComponent(handle)}${json ? '.js' : ''}`, base).href;
}

export function imageURL(value, origin) {
  if (typeof value !== 'string' || !value) return null;
  try {
    const url = new URL(value, origin);
    if (url.protocol !== 'https:' || (url.origin !== origin && url.hostname !== 'cdn.shopify.com')) return null;
    url.searchParams.set('width', '480');
    return url.href;
  } catch {
    return null;
  }
}

export async function fetchFavorite(fetcher, root, handle, origin, signal) {
  try {
    const response = await fetcher(productURL(root, handle, origin, true), {
      signal,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 404 || response.status === 410) return { ok: false, reason: 'unavailable' };
    if (!response.ok) return { ok: false, reason: 'load_failed' };
    const data = await response.json();
    if (signal?.aborted) return { ok: false, reason: 'aborted' };
    if (!data || data.handle !== handle || typeof data.title !== 'string' || !data.title.trim()) return { ok: false, reason: 'load_failed' };
    const productId = String(data.id || '').split('/').pop();
    const excludedVariant = Array.isArray(data.variants) && data.variants.some((variant) => String(variant?.id || '').split('/').pop() === EXCLUDED_VARIANT_ID);
    if (productId === EXCLUDED_PRODUCT_ID || excludedVariant || data.handle === EXCLUDED_HANDLE) return { ok: false, reason: 'excluded' };
    return {
      ok: true,
      title: data.title,
      image: imageURL(data.featured_image, origin),
      available: typeof data.available === 'boolean' ? data.available : null,
    };
  } catch {
    return { ok: false, reason: signal?.aborted ? 'aborted' : 'load_failed' };
  }
}

function configFor(element) {
  try {
    return JSON.parse(element.dataset.vgConfig || '{}');
  } catch {
    return {};
  }
}

function broadcast(source) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { source } }));
}

const browserStorage = () => window.localStorage;

class VgFavoriteButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    super.disconnectedCallback();
  }

  initialize() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.config = configFor(this);
    this.button = this.querySelector('[data-vg-favorite-toggle]');
    this.status = this.querySelector('[data-vg-favorite-status]');
    if (!(this.button instanceof HTMLButtonElement)) return;
    this.button.hidden = false;
    const options = { signal: this.listeners.signal };
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const result = toggleFavorite(browserStorage, this.dataset.handle);
      this.refresh();
      if (this.status) this.status.textContent = this.config[result.ok ? (result.saved ? 'saved_message' : 'removed_message') : result.reason] || '';
      if (result.ok) broadcast(this);
    }, options);
    window.addEventListener(CHANGE_EVENT, () => this.refresh(), options);
    window.addEventListener('pageshow', () => this.refresh(), options);
    window.addEventListener('storage', (event) => {
      if (event.key === FAVORITES_KEY || event.key === null) this.refresh();
    }, options);
    this.refresh();
  }

  refresh() {
    const current = readFavorites(browserStorage);
    const saved = current.ok && current.handles.includes(this.dataset.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    const label = this.config[saved ? 'saved_label' : 'save_label'] || '';
    this.button.setAttribute('aria-label', this.config.product_title ? `${label}: ${this.config.product_title}` : label);
    this.button.disabled = !validHandle(this.dataset.handle);
    if (!current.ok && this.status) this.status.textContent = this.config.storage_error || '';
  }
}

class VgFavorites extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.requests?.abort();
    super.disconnectedCallback();
  }

  initialize() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.config = configFor(this);
    this.list = this.querySelector('[data-vg-favorites-list]');
    this.status = this.querySelector('[data-vg-favorites-status]');
    this.empty = this.querySelector('[data-vg-favorites-empty]');
    this.clear = this.querySelector('[data-vg-favorites-clear]');
    if (!this.list || !this.status || !this.empty) return;
    const options = { signal: this.listeners.signal };
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const remove = event.target.closest('[data-vg-favorite-remove]');
      const clear = event.target.closest('[data-vg-favorites-clear]');
      if (!remove && !clear) return;
      event.preventDefault();
      const rows = [...this.list.children];
      const index = remove ? rows.indexOf(remove.closest('li')) : 0;
      const result = remove ? removeFavorite(browserStorage, remove.dataset.vgFavoriteRemove) : clearFavorites(browserStorage);
      if (!result.ok) {
        this.status.textContent = this.config.storage_error || '';
        return;
      }
      this.refresh(remove ? this.config.removed_message : this.config.cleared_message);
      const next = this.list.children[Math.min(index, this.list.children.length - 1)]?.querySelector('button');
      if (next) next.focus();
      else this.querySelector('[data-vg-favorites-heading]')?.focus();
      broadcast(this);
    }, options);
    window.addEventListener(CHANGE_EVENT, (event) => {
      if (event.detail?.source !== this) this.refresh();
    }, options);
    window.addEventListener('pageshow', () => this.refresh(), options);
    window.addEventListener('storage', (event) => {
      if (event.key === FAVORITES_KEY || event.key === null) this.refresh();
    }, options);
    this.refresh();
  }

  refresh(message = '') {
    this.requests?.abort();
    this.requests = new AbortController();
    const signal = this.requests.signal;
    const current = readFavorites(browserStorage);
    const focusedHandle = this.list.contains(document.activeElement) ? document.activeElement.closest('[data-vg-favorite-row]')?.dataset.vgFavoriteRow : null;
    this.list.replaceChildren();
    this.list.setAttribute('aria-busy', String(current.ok && current.handles.length > 0));
    this.empty.hidden = !current.ok || current.handles.length > 0;
    if (this.clear) this.clear.hidden = current.ok && current.handles.length === 0;
    this.status.textContent = message || (!current.ok ? this.config.storage_error : '') || '';
    if (!current.ok) return;
    const jobs = [];
    for (const handle of current.handles) {
      const card = document.createElement('li');
      card.className = 'vg-favorites__item';
      card.dataset.vgFavoriteRow = handle;
      const content = document.createElement('div');
      content.className = 'vg-favorites__content';
      const label = document.createElement('p');
      label.textContent = this.config.loading || '';
      content.append(label);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'vg-favorites__remove';
      remove.dataset.vgFavoriteRemove = handle;
      remove.textContent = this.config.remove || '';
      remove.setAttribute('aria-label', `${this.config.remove || ''}: ${handle}`);
      card.append(content, remove);
      this.list.append(card);
      if (focusedHandle === handle) remove.focus();
      jobs.push({ handle, content, remove });
    }
    if (focusedHandle && !current.handles.includes(focusedHandle)) this.querySelector('[data-vg-favorites-heading]')?.focus();
    const run = async () => {
      while (jobs.length && !signal.aborted) {
        const job = jobs.shift();
        const result = await fetchFavorite(window.fetch.bind(window), this.config.root, job.handle, window.location.origin, signal);
        if (signal.aborted || !this.isConnected || !this.list.contains(job.content)) return;
        this.renderProduct(job, result);
      }
    };
    const workers = Array.from({ length: Math.min(4, jobs.length) }, () => run());
    void Promise.all(workers).then(() => {
      if (!signal.aborted && this.isConnected) this.list.setAttribute('aria-busy', 'false');
    });
  }

  renderProduct({ handle, content, remove }, result) {
    content.replaceChildren();
    if (result.reason === 'excluded') {
      const removed = removeFavorite(browserStorage, handle);
      content.closest('[data-vg-favorite-row]')?.remove();
      if (removed.ok) {
        this.empty.hidden = removed.handles.length > 0;
        if (this.clear) this.clear.hidden = removed.handles.length === 0;
        broadcast(this);
      }
      return;
    }
    if (!result.ok) {
      const title = document.createElement('h3');
      title.textContent = handle.replaceAll('-', ' ');
      const message = document.createElement('p');
      message.textContent = this.config[result.reason] || this.config.load_failed || '';
      content.append(title, message);
      if (result.reason !== 'unavailable') this.appendProductLink(content, handle);
      return;
    }
    if (result.image) {
      const image = document.createElement('img');
      image.src = result.image;
      image.alt = '';
      image.width = 240;
      image.height = 240;
      image.loading = 'lazy';
      content.append(image);
    }
    const title = document.createElement('h3');
    title.textContent = result.title;
    content.append(title);
    if (result.available === false) {
      const availability = document.createElement('p');
      availability.textContent = this.config.sold_out || '';
      content.append(availability);
    }
    this.appendProductLink(content, handle);
    remove.setAttribute('aria-label', `${this.config.remove || ''}: ${result.title}`);
  }

  appendProductLink(content, handle) {
    try {
      const link = document.createElement('a');
      link.href = productURL(this.config.root, handle, window.location.origin);
      link.className = 'vg-favorites__link';
      link.textContent = this.config.view_product || '';
      content.append(link);
    } catch {
      this.status.textContent = this.config.load_failed || '';
    }
  }
}

if (!customElements.get('vg-favorite-button')) customElements.define('vg-favorite-button', VgFavoriteButton);
if (!customElements.get('vg-favorites')) customElements.define('vg-favorites', VgFavorites);
